/**
 * N+1 query detection — deterministic burst analysis over observed database
 * queries.
 *
 * The app talks to Postgres through PostgREST, so an N+1 shows up as the same
 * request shape repeated once per row: one list read followed by a lookup per
 * result. This module composes the query shape (`query-shape.ts`), the rolling
 * window (`query-burst-tracker.ts`), and bounded history (`bounded-list.ts`)
 * into the detector the backend boundary records into.
 *
 * Filter *values* are never captured, so reports and logs cannot leak customer
 * data. Pure and clock-injectable: the detector holds no timers and no I/O.
 */

import { createBoundedList } from "./bounded-list";
import { type BurstStatus, type BurstWindow, createBurstTracker } from "./query-burst-tracker";
import {
  buildRemediation,
  matchesSuppression,
  type ObservedQuery,
  querySignature,
  repeatsByDesign,
} from "./query-shape";

export interface NPlusOneReport {
  /** Repeat count of this signature inside the window at report time. */
  readonly count: number;
  readonly filterColumns: readonly string[];
  readonly firstSeenAt: number;
  readonly lastSeenAt: number;
  readonly operation: ObservedQuery["operation"];
  /** Actionable batching hint derived from the query shape. */
  readonly remediation: string;
  readonly signature: string;
  readonly table: string;
  readonly windowMs: number;
}

export interface NPlusOneDetectorOptions {
  /** Max queries and reports retained for inspection. Default 200. */
  readonly historyLimit?: number;
  readonly onReport?: (report: NPlusOneReport) => void;
  /**
   * Signatures or table/rpc names whose repeats are known-good and must never
   * report. Escape hatch for a shape the detector cannot tell apart from an
   * N+1 — a warning nobody can act on is what gets a detector switched off.
   */
  readonly suppressed?: readonly string[];
  /** Repeats of one signature inside the window that count as N+1. Default 5. */
  readonly threshold?: number;
  /** Rolling window in milliseconds. Default 1000. */
  readonly windowMs?: number;
}

export interface NPlusOneDetector {
  /** Queries recorded so far, oldest first, capped at `historyLimit`. */
  queries(): readonly ObservedQuery[];
  /** Records a query and returns a report when this call crossed a threshold. */
  record(query: ObservedQuery): NPlusOneReport | null;
  /** Reports emitted so far, oldest first, capped at `historyLimit`. */
  reports(): readonly NPlusOneReport[];
  reset(): void;
}

/** Range a configured number is held to, with the default it falls back to. */
interface OptionBounds {
  readonly fallback: number;
  readonly minimum: number;
}

const THRESHOLD_BOUNDS: OptionBounds = { fallback: 5, minimum: 2 };
const WINDOW_BOUNDS: OptionBounds = { fallback: 1000, minimum: 1 };
const HISTORY_BOUNDS: OptionBounds = { fallback: 200, minimum: 1 };

/** Falls back to the default for non-finite input so detection cannot silently stop. */
function clampOption(value: number | undefined, bounds: OptionBounds): number {
  if (!Number.isFinite(value)) {
    return bounds.fallback;
  }
  return Math.max(bounds.minimum, value as number);
}

interface ReportContext {
  readonly query: ObservedQuery;
  readonly status: BurstStatus;
  readonly window: BurstWindow;
}

function buildReport(context: ReportContext): NPlusOneReport {
  const { query, status, window } = context;
  return {
    count: status.count,
    filterColumns: [...query.filterColumns],
    firstSeenAt: status.firstSeenAt,
    lastSeenAt: query.startedAt,
    operation: query.operation,
    remediation: buildRemediation(query),
    signature: status.signature,
    table: query.table,
    windowMs: window.windowMs,
  };
}

/**
 * Creates a detector. Reports escalate by doubling (threshold, 2x, 4x, …) so a
 * sustained burst stays visible without flooding logs, and a signature that
 * falls quiet for a full window reports again on its next burst.
 */
export function createNPlusOneDetector(options: NPlusOneDetectorOptions = {}): NPlusOneDetector {
  const window: BurstWindow = {
    threshold: clampOption(options.threshold, THRESHOLD_BOUNDS),
    windowMs: clampOption(options.windowMs, WINDOW_BOUNDS),
  };
  const historyLimit = clampOption(options.historyLimit, HISTORY_BOUNDS);
  const { onReport } = options;
  const suppressed = options.suppressed ?? [];

  const tracker = createBurstTracker(window);
  const history = createBoundedList<ObservedQuery>(historyLimit);
  const emitted = createBoundedList<NPlusOneReport>(historyLimit);

  function emitReport(report: NPlusOneReport): void {
    try {
      onReport?.(report);
    } catch {
      // A failing consumer must not corrupt detection or the caller's query.
    }
  }

  function isSuppressed(query: ObservedQuery): boolean {
    if (suppressed.length === 0) {
      return false;
    }
    return matchesSuppression(suppressed, {
      signature: querySignature(query),
      table: query.table,
    });
  }

  return {
    queries() {
      return history.snapshot();
    },

    record(query) {
      history.push(query);
      if (repeatsByDesign(query) || isSuppressed(query)) {
        // Batched and paged reads repeat the same shape on purpose, and an
        // allowlisted shape was already judged. Still recorded, so query-budget
        // assertions keep counting them.
        return null;
      }

      const status = tracker.track(query);
      if (!status.shouldReport) {
        return null;
      }

      const report = buildReport({ query, status, window });
      emitted.push(report);
      emitReport(report);
      return report;
    },

    reports() {
      return emitted.snapshot();
    },

    reset() {
      tracker.reset();
      history.clear();
      emitted.clear();
    },
  };
}
